import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useUser } from '../auth/useUser';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { useGenerator } from '../features/generator/context/GeneratorContext';
import SEO from '../components/SEO';
import CreditTransactionLog from '../components/CreditTransactionLog';

// Fetch session history from API
async function fetchSessionHistory(getAccessTokenSilently, offset = 0, limit = 20) {
    const token = await getAccessTokenSilently();
    const response = await fetch(`/api/v1/users/me/sessions?limit=${limit}&offset=${offset}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        throw new Error('Failed to fetch session history');
    }

    return response.json();
}

// Session type badge component
function SessionTypeBadge({ type }) {
    const config = {
        generate: { label: 'Generate', class: 'bg-violet-500/10 dark:bg-violet-500/20 border-violet-200 dark:border-violet-400/30 text-violet-700 dark:text-violet-200' },
        refine: { label: 'Refine', class: 'bg-blue-500/10 dark:bg-blue-500/20 border-blue-200 dark:border-blue-400/30 text-blue-700 dark:text-blue-200' },
        upload: { label: 'Upload', class: 'bg-amber-500/10 dark:bg-amber-500/20 border-amber-200 dark:border-amber-400/30 text-amber-700 dark:text-amber-200' },
    };
    const { label, class: className } = config[type] || config.generate;
    return <span className={`badge text-xs ${className}`}>{label}</span>;
}

export default function ProfilePage() {
    const navigate = useNavigate();
    const { isAuthenticated, isLoading: authLoading, loginWithRedirect, getAccessTokenSilently } = useAuth0();
    const {
        user,
        isUserLoading,
        credits,
        monthlyCredits,
        topupCredits,
        monthlyCreditsExpiresAt,
        downloadCost,
        subscriptionTier,
        refetchUserInfo,
        resendVerificationEmail,
        requestPasswordResetEmail,
    } = useUser();

    // Use the hook for subscription status logic
    const {
        isPro,
        isCancelled,
        label: subscriptionLabel,
        subscriptionExpiresAt,
        scheduledCancellation,
        cancelledAt,
        hasProSubscription: _hasProSubscription // keep unused alias if strict linting but mostly replacing usage
    } = useSubscriptionStatus();

    // Backward compatibility if needed inside this component local scope
    const hasProSubscription = isPro;
    const { actions } = useGenerator();

    const [sessions, setSessions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const LIMIT = 20;

    const [activeTab, setActiveTab] = useState('generations');
    const [isCancelling, setIsCancelling] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelError, setCancelError] = useState(null);
    const [isResending, setIsResending] = useState(false);
    const [resendStatus, setResendStatus] = useState(null); // 'success' | 'error'

    const fetchSessions = useCallback(async (currentOffset = 0) => {
        setIsLoading(true);
        try {
            const result = await fetchSessionHistory(getAccessTokenSilently, currentOffset, LIMIT);
            const fetchedSessions = Array.isArray(result) ? result : (result.sessions || result.content || []);
            if (currentOffset === 0) {
                setSessions(fetchedSessions);
            } else {
                setSessions(prev => [...prev, ...fetchedSessions]);
            }
            setHasMore(fetchedSessions.length === LIMIT);
        } catch (err) {
            console.error('Failed to fetch sessions:', err);
        } finally {
            setIsLoading(false);
        }
    }, [getAccessTokenSilently]);

    useEffect(() => {
        if (isAuthenticated && !authLoading) {
            fetchSessions(0);
        }
    }, [isAuthenticated, authLoading, fetchSessions]);

    const loadMore = () => {
        const nextOffset = offset + LIMIT;
        setOffset(nextOffset);
        fetchSessions(nextOffset);
    };

    const handleRefine = (logo) => {
        actions.setActiveLogo(logo);
        navigate('/');
    };

    const handleView = (logoId) => {
        navigate(`/finalize/${logoId}`);
    };
    const handleCancelSubscription = async () => {
        setIsCancelling(true);
        setCancelError(null);
        try {
            const success = await cancelSubscription();
            if (success) {
                setShowCancelModal(false);
                // The context will update scheduledCancellation automatically
                // but we can also refetch user info to be sure if needed
                await refetchUserInfo();
            } else {
                setCancelError('Failed to cancel subscription. Please try again or contact support.');
            }
        } catch (err) {
            console.error('Error cancelling subscription:', err);
            setCancelError('An unexpected error occurred. Please try again.');
        } finally {
            setIsCancelling(false);
        }
    };

    const handleResendVerification = async () => {
        setIsResending(true);
        setResendStatus(null);
        try {
            const success = await resendVerificationEmail();
            if (success) {
                setResendStatus('success');
                // Status will clear after 5 seconds
                setTimeout(() => setResendStatus(null), 5000);
            } else {
                setResendStatus('error');
            }
        } catch (err) {
            console.error('Error resending verification:', err);
            setResendStatus('error');
        } finally {
            setIsResending(false);
        }
    };

    const handlePasswordReset = async () => {
        setIsResending(true);
        try {
            const success = await requestPasswordResetEmail();
            if (success) {
                alert('A password reset email has been sent to your inbox.');
            } else {
                alert('Failed to send password reset email. Please try again or contact support.');
            }
        } catch (err) {
            console.error('Error requesting password reset email:', err);
            alert('An unexpected error occurred.');
        } finally {
            setIsResending(false);
        }
    };

    // Redirect guests to login
    if (!authLoading && !isAuthenticated) {
        return (
            <main className="flex-1 app-scroll">
                <div className="container-page py-10 sm:py-14">
                    <div className="max-w-md mx-auto text-center glass p-8">
                        <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-2xl flex items-center justify-center shadow-[0_16px_30px_-20px_rgba(168,85,247,0.70)] border border-white/10">
                            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mb-2">Sign in to view your profile</h1>
                        <p className="text-slate-600 dark:text-slate-400 mb-6">Access your generation history and manage your subscription.</p>
                        <button onClick={() => loginWithRedirect()} className="btn-primary w-full py-2.5">
                            Sign in
                        </button>
                    </div>
                </div>
            </main>
        );
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    /* 
     * formatRenewalDate removed - logic moved to useSubscriptionStatus hook 
     */

    return (
        <main className="flex-1 app-scroll">
            <SEO
                title="Profile"
                description="Manage your AuraIcon account, view generation history, and track your credits."
                noindex={true}
            />
            <div className="container-page py-10 sm:py-14">
                <div className="max-w-5xl mx-auto">
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-xl font-bold text-white shadow-[0_12px_30px_-15px_rgba(168,85,247,0.6)] border border-white/10">
                            {user?.name?.[0] || user?.email?.[0] || '?'}
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">{user?.name || user?.email || 'User'}</h1>
                            <div className="flex flex-wrap items-center gap-3">
                                <p className="text-slate-600 dark:text-slate-400">{user?.email}</p>
                                {user && !user.email_verified && !isUserLoading && (
                                    <div className="flex items-center gap-2">
                                        <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                            Unverified
                                        </span>
                                        <button
                                            onClick={handleResendVerification}
                                            disabled={isResending || resendStatus === 'success'}
                                            className="text-[10px] font-bold text-violet-500 hover:text-violet-600 dark:hover:text-violet-400 disabled:text-slate-400 transition-colors uppercase tracking-widest underline underline-offset-2 decoration-violet-500/30"
                                        >
                                            {isResending ? 'Sending...' : resendStatus === 'success' ? 'Sent!' : 'Resend Email'}
                                        </button>
                                        {resendStatus === 'error' && (
                                            <span className="text-[10px] text-red-500 font-medium">Failed to send</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-6 mb-10">
                        {/* Subscription Card */}
                        <div className="glass p-5 flex flex-col h-full">
                            <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">Subscription</h2>
                            <div className="flex-1 space-y-4">
                                <div>
                                    <div className="flex justify-between items-center">
                                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Current Plan</p>
                                        <span className={`badge ${isPro ? 'badge-brand text-xs' : 'text-xs'}`}>
                                            {isPro ? 'Pro' : 'Free'}
                                        </span>
                                    </div>
                                    {isPro && subscriptionExpiresAt && !isCancelled && (
                                        <div className="flex flex-col items-end mt-0.5">
                                            <p className="text-[10px] text-violet-500 dark:text-violet-400 font-medium whitespace-nowrap">
                                                {subscriptionLabel}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {isPro && isCancelled && (
                                    <div className="pt-3 border-t border-slate-200 dark:border-white/5">
                                        <p className="text-[11px] text-red-600 dark:text-red-400 font-medium text-right uppercase tracking-wider">
                                            {/* Using the label from hook which already handles "Ends {date}" format logic 
                                                but here we want "Cancelled (Ends ...)" wrapper or just match hook output.
                                                The hook returns "Ends Feb 2, 2026". 
                                                The Profile UI had "Cancelled (Ends ...)" 
                                                Let's reconstruct slightly to match desired UI or trust the hook label.
                                                The hook label is concise. The UI wants "Cancelled" visible.
                                            */}
                                            Cancelled ({subscriptionLabel})
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="mt-6 space-y-2">
                                <button
                                    onClick={() => navigate('/pricing')}
                                    className="btn-primary w-full py-2 text-sm"
                                >
                                    {hasProSubscription ? 'Manage Subscription' : 'Upgrade to Pro'}
                                </button>
                                {hasProSubscription && !scheduledCancellation && (
                                    <button
                                        onClick={() => setShowCancelModal(true)}
                                        className="w-full py-1 text-[10px] text-slate-400 hover:text-red-500 transition-colors uppercase tracking-widest font-semibold"
                                    >
                                        Cancel Subscription
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Credits Card */}
                        <div className="glass p-5 flex flex-col h-full">
                            <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">Credits Balance</h2>
                            <div className="flex-1 space-y-4">
                                <div>
                                    <div className="flex justify-between items-baseline">
                                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Monthly Allotment</p>
                                        <p className="text-2xl font-bold text-slate-900 dark:text-slate-50">{monthlyCredits}</p>
                                    </div>
                                    <div className="flex flex-col items-end mt-0.5">
                                        {monthlyCreditsExpiresAt && (
                                            <p className="text-[10px] text-slate-500 dark:text-slate-500">
                                                Expires on {formatDate(monthlyCreditsExpiresAt)}
                                            </p>
                                        )}
                                        {isPro && subscriptionExpiresAt && !isCancelled && (
                                            <p className="text-[10px] text-violet-500 dark:text-violet-400 font-medium whitespace-nowrap">
                                                {subscriptionLabel}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="pt-3 border-t border-slate-200 dark:border-white/5">
                                    <div className="flex justify-between items-baseline">
                                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Top-up Credits</p>
                                        <p className="text-2xl font-bold text-slate-900 dark:text-slate-50">{topupCredits}</p>
                                    </div>
                                    <p className="text-[10px] text-slate-500 dark:text-slate-500 text-right mt-0.5 uppercase tracking-wider">
                                        Non-expiring
                                    </p>
                                </div>
                            </div>
                            <div className="mt-6">
                                <button
                                    onClick={() => navigate('/pricing')}
                                    className="btn-primary w-full py-2 text-sm"
                                >
                                    Get more credits
                                </button>
                            </div>
                        </div>

                        {/* Quick Actions Card */}
                        <div className="glass p-5 flex flex-col h-full">
                            <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">Quick Actions</h2>
                            <div className="flex-1 space-y-2">
                                <button
                                    onClick={() => navigate('/')}
                                    className="btn-ghost w-full py-2.5 text-sm text-left justify-start border border-slate-200 dark:border-white/5 hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl transition-all"
                                >
                                    <svg className="w-4 h-4 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                                    </svg>
                                    Create New Icon
                                </button>
                                <button
                                    onClick={() => {
                                        setActiveTab('credits');
                                        setTimeout(() => {
                                            const el = document.getElementById('history-section');
                                            if (el) el.scrollIntoView({ behavior: 'smooth' });
                                        }, 100);
                                    }}
                                    className="btn-ghost w-full py-2.5 text-sm text-left justify-start border border-slate-200 dark:border-white/5 hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl transition-all"
                                >
                                    <svg className="w-4 h-4 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    View Credit History
                                </button>
                                <button
                                    onClick={handlePasswordReset}
                                    className="btn-ghost w-full py-2.5 text-sm text-left justify-start border border-slate-200 dark:border-white/5 hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl transition-all"
                                >
                                    <svg className="w-4 h-4 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                    Change Password
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* History Section */}
                    <div id="history-section" className="mt-10">
                        {/* Tab Switcher */}
                        <div className="flex border-b border-slate-200 dark:border-white/5 mb-6">
                            <button
                                onClick={() => setActiveTab('generations')}
                                className={`px-6 py-3 text-sm font-medium transition-colors relative ${activeTab === 'generations'
                                    ? 'text-violet-500 dark:text-violet-400'
                                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                                    }`}
                            >
                                Generation History
                                {activeTab === 'generations' && (
                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-violet-500 dark:bg-violet-400" />
                                )}
                            </button>
                            <button
                                onClick={() => setActiveTab('credits')}
                                className={`px-6 py-3 text-sm font-medium transition-colors relative ${activeTab === 'credits'
                                    ? 'text-violet-500 dark:text-violet-400'
                                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                                    }`}
                            >
                                Credit History
                                {activeTab === 'credits' && (
                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-violet-500 dark:bg-violet-400" />
                                )}
                            </button>
                        </div>

                        {activeTab === 'generations' ? (
                            <div>
                                {authLoading || (isLoading && sessions.length === 0) ? (
                                    <div className="glass p-8 text-center">
                                        <div className="animate-spin w-8 h-8 border-2 border-violet-400 border-t-transparent rounded-full mx-auto mb-3" />
                                        <p className="text-slate-600 dark:text-slate-400">Loading your history...</p>
                                    </div>
                                ) : sessions.length === 0 ? (
                                    <div className="glass p-8 text-center">
                                        <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 dark:bg-white/5 rounded-2xl flex items-center justify-center border border-slate-200 dark:border-white/10">
                                            <svg className="w-8 h-8 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                                            </svg>
                                        </div>
                                        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-1">No history yet</h3>
                                        <p className="help mb-4">Start generating icons to see your history here.</p>
                                        <button onClick={() => navigate('/')} className="btn-primary px-6 py-2">
                                            Create your first icon
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="space-y-4">
                                            {sessions.map((session) => (
                                                <div key={session.id} className="glass p-4">
                                                    {/* Session Header */}
                                                    <div className="flex items-start justify-between mb-3">
                                                        <div className="flex items-center gap-3">
                                                            <SessionTypeBadge type={session.type} />
                                                            <span className="text-sm text-slate-500 dark:text-slate-400">
                                                                {formatDate(session.createdAt)}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Prompt */}
                                                    {session.prompt && (
                                                        <p className="text-sm text-slate-700 dark:text-slate-300 mb-3 line-clamp-2">
                                                            "{session.prompt}"
                                                        </p>
                                                    )}

                                                    {/* Logos Grid */}
                                                    {session.logos && session.logos.length > 0 && (
                                                        <div className="grid grid-cols-4 gap-2">
                                                            {session.logos.map((logo) => (
                                                                <div
                                                                    key={logo.id}
                                                                    className="group relative aspect-square rounded-lg overflow-hidden border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 dark:bg-white/[0.02] cursor-pointer hover:border-violet-400/30 transition-all"
                                                                    onClick={() => handleView(logo.id)}
                                                                >
                                                                    <img
                                                                        src={logo.thumbnailUrl || logo.url}
                                                                        alt="Generated logo"
                                                                        className="w-full h-full object-contain"
                                                                    />

                                                                    {/* Purchased badge */}
                                                                    {logo.isPurchased && (
                                                                        <div className="absolute top-1 right-1">
                                                                            <svg className="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                            </svg>
                                                                        </div>
                                                                    )}

                                                                    {/* Hover overlay */}
                                                                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleRefine(logo);
                                                                            }}
                                                                            className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs"
                                                                            title="Refine"
                                                                        >
                                                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                                            </svg>
                                                                        </button>
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleView(logo.id);
                                                                            }}
                                                                            className="p-1.5 rounded-lg bg-violet-500/80 hover:bg-violet-500 text-white text-xs"
                                                                            title="View / Download"
                                                                        >
                                                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                                            </svg>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        {hasMore && (
                                            <div className="text-center mt-6">
                                                <button
                                                    onClick={loadMore}
                                                    disabled={isLoading}
                                                    className="btn-secondary px-6 py-2"
                                                >
                                                    {isLoading ? (
                                                        <>
                                                            <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                                                            Loading...
                                                        </>
                                                    ) : (
                                                        'Load More'
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : (
                            <CreditTransactionLog />
                        )}
                    </div>
                </div>
            </div>
            {/* Cancellation Confirmation Modal */}
            {showCancelModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="glass max-w-md w-full p-6 shadow-2xl animate-in zoom-in-95 duration-200">
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Cancel Subscription?</h3>
                        <p className="text-slate-600 dark:text-slate-400 mb-6">
                            Your subscription will remain active until <strong>{subscriptionLabel.replace('Renews ', '').replace('Ends ', '')}</strong>. After that, you will lose Pro benefits like unlimited generations.
                        </p>

                        {cancelError && (
                            <div className="p-3 mb-4 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-600 dark:text-red-400">
                                {cancelError}
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowCancelModal(false)}
                                disabled={isCancelling}
                                className="btn-ghost flex-1 py-2 text-sm"
                            >
                                Stay Pro
                            </button>
                            <button
                                onClick={handleCancelSubscription}
                                disabled={isCancelling}
                                className="btn-primary bg-red-500 hover:bg-red-600 shadow-[0_10px_20px_-10px_rgba(239,68,68,0.5)] border-red-400/20 flex-1 py-2 text-sm text-white"
                            >
                                {isCancelling ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Cancelling...
                                    </div>
                                ) : (
                                    'Confirm Cancellation'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main >
    );
}
